import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { HouseholdSize } from '@/components/client/MedianIncomePromo/useMedianIncomeState';
import type { MedianIncomeYear } from '../constants';
import { HOUSEHOLD_OPTIONS, MEDIAN_INCOME_BY_YEAR } from '../constants';
interface TierTableProps {
  year: MedianIncomeYear;
  /** 계산기에서 선택한 가구원수(1~7). 이 컬럼 하나만 노출한다. */
  householdSize: HouseholdSize;
  /** 계산된 가장 가까운 구간(%). 매칭 row 를 강조한다. null 이면 강조 없음. */
  activeTier: number | null;
  /** Row 클릭 시 우측 계산기에 해당 % 값을 반영한다. */
  onTierClick: (percent: number) => void;
}

/** 항상 강조되는 기준선(중위소득 100%). */
const BASELINE_TIER = 100;
const formatNumber = (n: number) => Math.round(n).toLocaleString('ko-KR');

/**
 * ④ 표1 — 250%~50% 구간 × 가구원수 1열.
 * - 사용자가 계산기에서 고른 가구원수에 해당하는 컬럼 하나만 동적으로 노출한다.
 * - `BASELINE_TIER`(100%) row 는 항상 강조, `activeTier` 와 일치하는 row 는 추가 강조.
 */
export const TierTable = t0 => {
  const $ = _c(39);
  const {
    year,
    householdSize,
    activeTier,
    onTierClick
  } = t0;
  const {
    householdBaselines,
    tiers,
    source
  } = MEDIAN_INCOME_BY_YEAR[year];
  const householdIndex = householdSize - 1;
  const baselineWon = householdBaselines[householdIndex] ?? 0;
  const householdLabel = HOUSEHOLD_OPTIONS[householdIndex]?.label ?? `${householdSize}인`;
  let t1;
  if ($[0] !== year) {
    t1 = <h2 className="font-700 text-Gray-900 typo-h5 mobile-tablet:typo-h6">{year}년 기준 중위소득</h2>;
    $[0] = year;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== baselineWon) {
    t2 = formatNumber(baselineWon);
    $[2] = baselineWon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <strong className="font-700 text-Gray-900">월 {t2}원</strong>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== householdLabel || $[7] !== t3 || $[8] !== year) {
    t4 = <p className="text-Gray-700 typo-body4">{year} {householdLabel} 가구 기준 100% 금액은{" "}{t3}이며, 중위소득 % 구간별로 신청 가능한 복지정책이 달라집니다.</p>;
    $[6] = householdLabel;
    $[7] = t3;
    $[8] = year;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== householdLabel || $[11] !== source || $[12] !== year) {
    t5 = <caption className="sr-only">{year}년 {householdLabel} 가구 기준 중위소득 구간별 월 금액 (출처{" "}{source})</caption>;
    $[10] = householdLabel;
    $[11] = source;
    $[12] = year;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <th scope="col" className="border-b-1 border-Gray-200 p-12 text-center font-600 typo-caption1">중위소득 %</th>;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== householdLabel) {
    t7 = <thead><tr className="bg-Gray-50 text-Gray-700">{t6}<th scope="col" className="border-b-1 border-Gray-200 p-12 text-center font-600 typo-caption1">{householdLabel} (원)</th></tr></thead>;
    $[15] = householdLabel;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== activeTier || $[18] !== baselineWon || $[19] !== onTierClick || $[20] !== tiers) {
    let t9;
    if ($[22] !== activeTier || $[23] !== baselineWon || $[24] !== onTierClick) {
      t9 = percent => {
        const isBaseline = percent === BASELINE_TIER;
        const isActive = activeTier === percent;
        return <tr key={percent} className={cn("cursor-pointer border-t-1 border-Gray-100 transition-colors", {
          "bg-Y-50": isBaseline && !isActive,
          "bg-B-50": isActive,
          "hover:bg-B-25": !isActive && !isBaseline
        })} onClick={() => onTierClick(percent)}><td className={cn("p-12 text-center font-700", isActive ? "text-B-600" : "text-Gray-900")}>{percent}%</td><td className={cn("p-12 text-center tabular-nums", isActive && "font-700 text-B-600")}>{formatNumber(baselineWon * percent / 100)}</td></tr>;
      };
      $[22] = activeTier;
      $[23] = baselineWon;
      $[24] = onTierClick;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t8 = tiers.map(t9);
    $[17] = activeTier;
    $[18] = baselineWon;
    $[19] = onTierClick;
    $[20] = tiers;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[26] !== t8) {
    t9 = <tbody>{t8}</tbody>;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== source) {
    t10 = <tfoot><tr><td colSpan={2} className="border-t-1 border-Gray-200 bg-Gray-25 px-12 py-8 text-center text-Gray-600 typo-caption2">단위: 월/원 · 출처 {source}</td></tr></tfoot>;
    $[28] = source;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t5 || $[32] !== t7 || $[33] !== t9) {
    t11 = <div className="overflow-hidden rounded-small border-1 border-Gray-200"><table className="w-full border-collapse text-Gray-800 typo-body4">{t5}{t7}{t9}{t10}</table></div>;
    $[30] = t10;
    $[31] = t5;
    $[32] = t7;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t1 || $[36] !== t11 || $[37] !== t4) {
    t12 = <section id="median-income-tier-table" className="flex scroll-mt-80 flex-col gap-12 mobile-tablet:scroll-mt-64 mobile-tablet:px-16">{t1}{t4}{t11}</section>;
    $[35] = t1;
    $[36] = t11;
    $[37] = t4;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};